import { useAuth } from "react-oidc-context";
import {
    createContext,
    useState,
    useEffect,
    useMemo,
    useContext
  } from 'react';
import { jwtDecode } from 'jwt-decode';
import userService from '../services/userService';

const TenantContext = createContext({
    tenant: null,
    setTenant: () => {},
    tenants: [],
    setTenants: () => {},
    hasTenant: false,
    isLoading: true,
    userRole: null,
    isImpersonating: false,
    impersonatingUser: null,
    impersonatingTenant: null,
    startImpersonation: () => {},
    stopImpersonation: () => {},
});

const TENANT_STORAGE_KEY = 'stackbd_selected_tenant';
const IMPERSONATE_USER_KEY = 'stackbd_impersonating_user';
const IMPERSONATE_EMAIL_KEY = 'stackbd_impersonating_email';
const IMPERSONATE_TENANT_KEY = 'stackbd_impersonating_tenant';

function TenantProvider({ children }) {
    const [tenant, setTenantState] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [hasTenant, setHasTenant] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [impersonatingUser, setImpersonatingUser] = useState(() => localStorage.getItem(IMPERSONATE_USER_KEY));
    const [impersonatingTenant, setImpersonatingTenant] = useState(() => localStorage.getItem(IMPERSONATE_TENANT_KEY));
    const auth = useAuth();

    const isImpersonating = !!impersonatingUser;

    const startImpersonation = (tenantId, username, email) => {
        localStorage.setItem(IMPERSONATE_USER_KEY, username);
        localStorage.setItem(IMPERSONATE_TENANT_KEY, tenantId);
        if (email) localStorage.setItem(IMPERSONATE_EMAIL_KEY, email);
        setImpersonatingUser(username);
        setImpersonatingTenant(tenantId);
    };

    const stopImpersonation = () => {
        localStorage.removeItem(IMPERSONATE_USER_KEY);
        localStorage.removeItem(IMPERSONATE_EMAIL_KEY);
        localStorage.removeItem(IMPERSONATE_TENANT_KEY);
        setImpersonatingUser(null);
        setImpersonatingTenant(null);
    };

    // Wrap setTenant to persist to localStorage
    const setTenant = (newTenant) => {
        setTenantState(newTenant);
        setUserRole(null); // Reset role when switching tenants
        if (newTenant) {
            localStorage.setItem(TENANT_STORAGE_KEY, newTenant);
        } else {
            localStorage.removeItem(TENANT_STORAGE_KEY);
        }
    };

    useEffect(() => {
        if(auth.user){
            const decodedIdToken = jwtDecode(auth.user.id_token);
            const customTenants = decodedIdToken['custom:tenants'];

            if (customTenants) {
                const tenantList = customTenants.split(',');
                setTenants(tenantList);

                // Check localStorage for previously selected tenant
                const savedTenant = localStorage.getItem(TENANT_STORAGE_KEY);
                if (savedTenant && tenantList.includes(savedTenant)) {
                    setTenantState(savedTenant);
                } else {
                    // Default to first tenant and save it
                    setTenantState(tenantList[0]);
                    localStorage.setItem(TENANT_STORAGE_KEY, tenantList[0]);
                }
                setHasTenant(true);
            } else {
                setTenants([]);
                setTenantState(null);
                localStorage.removeItem(TENANT_STORAGE_KEY);
                setHasTenant(false);
            }
            setIsLoading(false);
        } else if (!auth.isLoading) {
            setIsLoading(false);
        }
    }, [auth])

    // Fetch user role whenever tenant changes
    useEffect(() => {
        if (auth.user?.id_token && tenant) {
            userService.getUserStatus(auth.user.id_token, tenant)
                .then(data => setUserRole(data.role || (data.is_owner ? 'owner' : 'member')))
                .catch(() => setUserRole('member'));
        }
    }, [auth.user?.id_token, tenant]);

    // Clear impersonation on logout
    useEffect(() => {
        if (!auth.user && !auth.isLoading) {
            stopImpersonation();
        }
    }, [auth.user, auth.isLoading]);

    const effectiveTenant = impersonatingTenant || tenant;
    const effectiveRole = isImpersonating ? 'owner' : userRole;

    const context = useMemo(() => {
        return {
          tenant: effectiveTenant,
          setTenant,
          tenants,
          setTenants,
          hasTenant,
          isLoading,
          userRole: effectiveRole,
          isImpersonating,
          impersonatingUser,
          impersonatingTenant,
          startImpersonation,
          stopImpersonation,
        };
      }, [
        effectiveTenant,
        tenants,
        hasTenant,
        isLoading,
        effectiveRole,
        isImpersonating,
        impersonatingUser,
        impersonatingTenant,
      ]);

    return (
        <TenantContext.Provider value={context}>{children}</TenantContext.Provider>
    );
}

export const useTenant = () => {
    return useContext(TenantContext);
};
export { TenantProvider, TenantContext }
